import { create } from 'zustand';
import { isAfter } from 'date-fns/isAfter';
import sitesApi from '../api/site';
import { Site } from '../common/types';
import { OrderingAvailabilityStatus } from '../common/enums';
import { latestPickupTime } from '../common/timeFunctions';

type SitesStore = {
  hasLoaded: boolean;
  sites: Site[];
  includeDisabled: boolean;
  isAcceptingOnlineOrders: (id: number | null) => boolean;
  getSiteOrderingAvailabilityStatus: (id: number | null) => OrderingAvailabilityStatus;
  loading: boolean;
  error: unknown | null;
  fetchSites: (force?: boolean, includeDisabled?: boolean) => void;
  addSite: (site: import('../api/site').CreateSiteRequest) => Promise<any>;
  updateSiteAction: (id: number | string, site: import('../api/site').CreateSiteRequest) => Promise<any>;
  removeSite: (id: number | string) => Promise<any>;
  disableSiteAction: (id: number | string) => Promise<any>;
  enableSiteAction: (id: number | string) => Promise<any>;
};

const useSites = create<SitesStore>((set, get) => ({
  hasLoaded: false,
  sites: [],
  includeDisabled: false,
  loading: false,
  error: null,
  fetchSites: async (force = false, includeDisabled?: boolean) => {
    if (get().loading && !force) {
      return;
    }
    const nextIncludeDisabled = includeDisabled ?? get().includeDisabled;
    try {
      set({ loading: true, hasLoaded: true, includeDisabled: nextIncludeDisabled });
      const result = await sitesApi.listSites(nextIncludeDisabled);
      set({ sites: result.data ?? [], loading: false, error: null });
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
    }
  },
  isAcceptingOnlineOrders: (id: number | null) => {
    if (id === null) {
      return true
    }
    const site = get().sites.find((s) => s.id === id);
    return site?.isAcceptingOnlineOrders ?? false;
  },
  getSiteOrderingAvailabilityStatus: (id: number | null): OrderingAvailabilityStatus => {
    if (id === null) {
      return OrderingAvailabilityStatus.Today
    }
    const site = get().sites.find((s) => s.id === id)
    if (!site) {
      // block it on the server, but seriously how did this happen?
      return OrderingAvailabilityStatus.Today
    }
    if (!site.isAcceptingOnlineOrders) {
      return OrderingAvailabilityStatus.Unavailable
    }
    const now = new Date()
    if (isAfter(now, latestPickupTime(now, site.orderingCloseTime))) {
      return OrderingAvailabilityStatus.Tomorrow
    }
    return OrderingAvailabilityStatus.Today
  },
  addSite: async (site) => {
    try {
      set({ loading: true });
      const response = await sitesApi.createSite(site);
      get().fetchSites(true);
      return response.data;
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
      throw err;
    }
  },
  updateSiteAction: async (id, site) => {
    try {
      set({ loading: true });
      const response = await sitesApi.updateSite(id, site);
      get().fetchSites(true);
      return response.data;
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
      throw err;
    }
  },
  removeSite: async (id) => {
    try {
      set({ loading: true });
      const response = await sitesApi.deleteSite(id);
      get().fetchSites(true);
      return response.data;
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
      throw err;
    }
  },
  disableSiteAction: async (id) => {
    try {
      set({ loading: true });
      const response = await sitesApi.disableSite(id);
      get().fetchSites(true);
      return response.data;
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
      throw err;
    }
  },
  enableSiteAction: async (id) => {
    try {
      set({ loading: true });
      const response = await sitesApi.enableSite(id);
      get().fetchSites(true);
      return response.data;
    } catch (err) {
      console.error(err);
      set({ loading: false, error: err });
      throw err;
    }
  },
}));

export default useSites;
export type { CreateSiteRequest } from '../api/site';
export type { Site as StoreSite };

// make that first call without having to useEffect in a component
if (!useSites.getState().hasLoaded) {
  useSites.getState().fetchSites();
}
